import { getAccessToken, logout } from './auth';

/**
 * Preisanfragen der Servicepartner.
 *
 * Ein Partner kann im Portal statt zu bestätigen einen anderen Preis anfragen;
 * das Gerät steht dann auf „Warte auf Preisfreigabe" und die Anfrage hier im
 * Dashboard. Zusage: der Betrag gilt für diesen Fall (die Rezeptur bleibt).
 * Absage: der bisherige Preis bleibt. Bestätigen muss der Partner danach so oder so.
 *
 * Eigener `request`-Helfer wie in den Schwesterdateien — im Haus hat jeder
 * Service seinen, statt einer gemeinsamen Abstraktion.
 */

export interface PriceRequestCost {
  state:                 'open' | 'requested' | 'confirmed';
  reference_price:       number | null;
  reference_price_label: string;
  price_request: {
    amount:             number;
    amount_label:       string;
    note:               string;
    timestamp:          string | null;
    decision:           'accepted' | 'rejected' | null;
    decision_note:      string | null;
    decision_timestamp: string | null;
  } | null;
}

export interface PriceRequest {
  stock_device_id: number;
  case_id:         number;
  stock_number:    string;
  /** Hersteller, Gerät, Farbe und Speicher in einer Zeile — wie im Versanddialog. */
  description:     string;
  imei_sn:         string | null;
  servicepartner:  { id: number; description: string };
  slip_number:     string;
  is_warranty:     boolean;
  error_pattern:   string;
  cost:            PriceRequestCost;
}

export interface DecisionResult {
  /** Gesetzt, wenn die Entscheidung steht, die Mail aber nicht abging. */
  mail_warning: string | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  items?: T[];
  item?: T;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T> & Record<string, unknown>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T> & Record<string, unknown>;
  try {
    body = (await resp.json()) as ApiEnvelope<T> & Record<string, unknown>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result);
  }

  return body;
}

/** Alle offenen Anfragen, älteste zuerst. */
export async function listOpenPriceRequests(): Promise<PriceRequest[]> {
  const body = await request<PriceRequest>(`${window.location.origin}/api/partner-price-requests`, { method: 'GET' });
  return body.items ?? [];
}

/** Zusagen oder ablehnen; die Begründung ist optional und geht in die Mail. */
export async function decidePriceRequest(stockDeviceId: number, accepted: boolean, note: string | null): Promise<DecisionResult> {
  const body = await request<never>(
    `${window.location.origin}/api/stock-devices/${stockDeviceId}/partner-price-request/decision`,
    { method: 'POST', body: JSON.stringify({ accepted, note }) },
  );
  return { mail_warning: (body.mail_warning as string | null) ?? null };
}
